<script setup lang="ts">
import {
  initializeBugTracker,
  type BugTrackerInitOptions,
  type BugTrackerInstance,
} from "@rtg-enterprise/bug-tracker-plugin";
import { onMounted, onUnmounted, ref } from "vue";
import { Toaster } from "vue-sonner";
import "vue-sonner/style.css";
import AboutModal from "./components/AboutModal.vue";
import ModalContainer from "./components/ModalContainer.vue";
import HelpSlideOver from "./features/help/HelpSlideOver.vue";
import { applyConfiguredRedistributionDefaults } from "./services/algorithmDefault.ts";
import { createBugTrackerPersistenceAdapter } from "./services/bugTrackerPersistence.ts";
import { useAppInfoStore } from "./stores/appInfoStore.ts";
import { useModalStore, type ModalOptions } from "./stores/modalStore.ts";

const appVersion = __APP_VERSION__;
const modalStore = useModalStore();
const appInfoStore = useAppInfoStore();
let bugTracker: BugTrackerInstance | null = null;
const showBugTrackerNav = ref(false);

function hasBugTrackerCookie(): boolean {
  return document.cookie
    .split(";")
    .map((part) => part.trim())
    .includes("bug-tracker=on");
}

function refreshBugTrackerNav(): void {
  showBugTrackerNav.value = hasBugTrackerCookie();
}

function openBugTracker(): void {
  bugTracker?.openTracker();
}

function resolveBugTrackerTheme(): "light" | "dark" {
  const documentTheme = document.documentElement.dataset.theme;
  if (documentTheme === "light" || documentTheme === "dark") {
    return documentTheme;
  }

  const storedTheme = localStorage.getItem("insight-iq-theme");
  if (storedTheme === "light" || storedTheme === "dark") {
    return storedTheme;
  }

  const mediaQuery =
    typeof globalThis.matchMedia === "function"
      ? globalThis.matchMedia("(prefers-color-scheme: dark)")
      : null;

  return mediaQuery?.matches ? "dark" : "light";
}

function initializeBugTrackerWithFallback(
  options: BugTrackerInitOptions
): BugTrackerInstance {
  try {
    return initializeBugTracker(options);
  } catch (error) {
    console.error(
      "[BugTracker] Failed to initialize shared persistence adapter. Falling back to local persistence.",
      error
    );
    const fallbackOptions: BugTrackerInitOptions = { ...options };
    delete fallbackOptions.persistenceAdapter;
    return initializeBugTracker(fallbackOptions);
  }
}

onMounted(() => {
  void appInfoStore.fetchApiVersion();
  void applyConfiguredRedistributionDefaults();

  const cookieDomain = globalThis.location.hostname.endsWith(
    ".ifredisku.example.com"
  )
    ? ".ifredisku.example.com"
    : undefined;

  const options: BugTrackerInitOptions = {
    theme: resolveBugTrackerTheme(),
    appId: "IFR_Redisku",
    persistenceAdapter: createBugTrackerPersistenceAdapter(),
    onReport: (payload) => {
      console.log("bug tracker payload", payload);
    },
  };
  if (cookieDomain) {
    options.cookieDomain = cookieDomain;
  }
  bugTracker = initializeBugTrackerWithFallback(options);
  refreshBugTrackerNav();
  globalThis.addEventListener("focus", refreshBugTrackerNav);
  globalThis.addEventListener("popstate", refreshBugTrackerNav);
});

onUnmounted(() => {
  globalThis.removeEventListener("focus", refreshBugTrackerNav);
  globalThis.removeEventListener("popstate", refreshBugTrackerNav);
  bugTracker?.destroy();
  bugTracker = null;
});

function showAbout(): void {
  const opts: ModalOptions = {
    width: 360,
    height: 220,
    draggable: true,
    closeOnEscapeKeyPressed: true,
  };
  modalStore.show(AboutModal, opts);
}
</script>

<template>
  <Toaster position="top-right" :duration="4000" rich-colors expand />
  <RouterView />
  <div class="app-version-label">
    <span class="app-version-value">v{{ appVersion }}</span>
    <div class="app-version-actions">
      <button
        v-if="showBugTrackerNav"
        type="button"
        class="app-version-info-btn app-version-info-btn--bug"
        aria-label="Bug Tracker"
        title="Open Bug Tracker"
        @click="openBugTracker"
      >
        BT
      </button>
      <RouterLink
        :to="{ name: 'dashboard-algorithm-trace-log' }"
        class="app-version-info-btn"
        aria-label="Algorithm Trace Log"
        title="Open Algorithm Trace Log"
      >
        TL
      </RouterLink>
      <button
        type="button"
        class="app-version-info-btn app-version-info-btn--about"
        aria-label="About"
        @click="showAbout"
      >
        ?
      </button>
    </div>
  </div>
  <ModalContainer />
  <HelpSlideOver />
</template>
